import { useEffect, useCallback, useRef, useState } from 'react';
import type { Stroke, PubSubStrokeCommit, ChannelConfig, DrawingSubmission } from '@twitch-draw/shared';
import { useTwitch } from '../twitch/useTwitch';
import { ebsClient } from '../lib/api';
import { DrawCanvas } from '../components/DrawCanvas';
import { Toolbar } from '../components/Toolbar';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ActiveDrawingsPanel } from '../components/ActiveDrawingsPanel';
import { AnimationControls } from '../components/AnimationControls';
import { ModerationPanel } from '../components/ModerationPanel';
import { playSound, preloadSound } from '../hooks/useSound';
import { useDrawingState } from '../hooks/useDrawingState';
import { useMonitoring } from '../hooks/useMonitoring';

const DEFAULT_DISPLAY_DURATION_MS = 30000;

const BITS_SKUS = [
  { sku: 'draw-7', bits: 10 },
  { sku: 'draw-8', bits: 25 },
  { sku: 'draw-9', bits: 50 },
  { sku: 'draw-1', bits: 100 },
  { sku: 'draw-2', bits: 300 },
  { sku: 'draw-3', bits: 500 },
  { sku: 'draw-4', bits: 1000 },
  { sku: 'draw-5', bits: 2000 },
  { sku: 'draw-6', bits: 5000 },
];

function getSkuForAmount(amount: number): { sku: string; bits: number } {
  for (const item of BITS_SKUS) {
    if (item.bits >= amount) {
      return item;
    }
  }
  return BITS_SKUS[BITS_SKUS.length - 1];
}

const CONSENT_STORAGE_KEY = 'drawgg:consent:v1';

export default function App() {
  const twitch = useTwitch();
  const { captureException } = useMonitoring(twitch.auth?.channelId);
  const [channelConfig, setChannelConfig] = useState<ChannelConfig | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [pendingSubmissions, setPendingSubmissions] = useState<DrawingSubmission[]>([]);
  const [modError, setModError] = useState<string | null>(null);
  const [modActionMap, setModActionMap] = useState<Record<string, boolean>>({});
  const [consentAccepted, setConsentAccepted] = useState(() => {
    try {
      return localStorage.getItem(CONSENT_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentDismissed, setConsentDismissed] = useState(false);

  const drawing = useDrawingState({
    channelId: twitch.auth?.channelId,
    userId: twitch.auth?.userId ?? 'anonymous',
    channelConfig,
    viewerDisplayName: 'You',
  });
  const {
    setStrokes,
    setAppState,
    pendingStrokesRef,
    setPendingStrokes,
    setTotalAmount,
  } = drawing;

  const displayDurationMs = channelConfig?.displayDurationMs ?? DEFAULT_DISPLAY_DURATION_MS;

  // Animation preview timer
  useEffect(() => {
    if (!drawing.isPreviewingAnimation || !drawing.isAnimationMode) return;
    if (drawing.timelineFrames.length === 0) return;
    const interval = setInterval(() => {
      drawing.setPreviewTick(prev => prev + 1);
    }, 1000 / drawing.animationFps);
    return () => clearInterval(interval);
  }, [drawing.isPreviewingAnimation, drawing.isAnimationMode, drawing.animationFps, drawing.timelineFrames.length]);
  const identityRequired = twitch.isReady && (!twitch.auth?.userId || !consentAccepted);
  const showConsentModal = twitch.isReady && !consentAccepted && !consentDismissed;
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submittedTxRef = useRef<Set<string>>(new Set());

  const showStatus = useCallback((message: string) => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setStatusMessage(message);
    statusTimeoutRef.current = setTimeout(() => {
      setStatusMessage(null);
      statusTimeoutRef.current = null;
    }, 6000);
  }, [setStatusMessage]);

  const setSubmissionBusy = useCallback((submissionId: string, busy: boolean) => {
    setModActionMap(prev => {
      const next = { ...prev };
      if (busy) {
        next[submissionId] = true;
      } else {
        delete next[submissionId];
      }
      return next;
    });
  }, [setModActionMap]);

  useEffect(() => {
    if (!twitch.isReady || !twitch.auth) return;

    ebsClient.setToken(twitch.auth.token);
    setAppState('drawing');

    Promise.all([
      ebsClient.getChannelConfig().then(res => setChannelConfig(res.config)),
      ebsClient.getState().then(res => {
        const now = Date.now();
        const alive = res.strokes.filter(s => now - s.createdAt < displayDurationMs);
        setStrokes(alive);
        alive.forEach(s => {
          const remaining = displayDurationMs - (now - s.createdAt);
          const timer = setTimeout(() => {
            setStrokes(prev => prev.filter(p => p.id !== s.id));
            expirationTimersRef.current.delete(s.id);
          }, remaining);
          expirationTimersRef.current.set(s.id, timer);
        });
      }),
    ]).catch(err => {
      captureException(err, { context: 'initialize_overlay' });
      console.error('Failed to initialize overlay:', err);
    });
  }, [twitch.isReady, twitch.auth, setAppState, setStrokes, captureException]);

  useEffect(() => {
    preloadSound('./sounds/draw.mp3');
    preloadSound('./sounds/alert.wav');
  }, []);

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  const expirationTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleStrokeExpiration = useCallback((strokeId: string) => {
    const existingTimer = expirationTimersRef.current.get(strokeId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      setStrokes(prev => prev.filter(s => s.id !== strokeId));
      expirationTimersRef.current.delete(strokeId);
    }, displayDurationMs);

    expirationTimersRef.current.set(strokeId, timer);
  }, [setStrokes, displayDurationMs]);

  useEffect(() => {
    return () => {
      expirationTimersRef.current.forEach(timer => clearTimeout(timer));
      expirationTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!twitch.isModerator || !twitch.auth) {
      setPendingSubmissions([]);
      setModError(null);
      return;
    }

    ebsClient.getPendingSubmissions()
      .then(res => {
        setPendingSubmissions(res.submissions);
        setModError(null);
      })
      .catch(err => {
        captureException(err, { context: 'load_pending_submissions' });
        setModError(err instanceof Error ? err.message : 'Failed to load submissions');
      });
  }, [twitch.isModerator, twitch.auth, setPendingSubmissions, setModError, captureException]);

  useEffect(() => {
    twitch.onPubSubMessage((message) => {
      if (message.t === 'stroke.commit') {
        const strokeMsg = message as PubSubStrokeCommit;
        const newStroke: Stroke = {
          id: strokeMsg.id,
          channelId: strokeMsg.c,
          userId: strokeMsg.u,
          displayName: strokeMsg.dn ?? 'Viewer',
          createdAt: strokeMsg.ts,
          color: strokeMsg.col,
          width: strokeMsg.w,
          durationMs: strokeMsg.ms,
          points: strokeMsg.p,
          ...(strokeMsg.anim ? { animation: strokeMsg.anim } : {}),
        };
        setStrokes(s => [...s, newStroke]);
        playSound('./sounds/draw.mp3', 0.4);

        scheduleStrokeExpiration(newStroke.id);
      } else if (message.t === 'stroke.added') {
        ebsClient.getState()
          .then(res => {
            const now = Date.now();
            const alive = res.strokes.filter(s => now - s.createdAt < displayDurationMs);
            setStrokes(alive);
            alive.forEach(s => {
              const remaining = displayDurationMs - (now - s.createdAt);
              if (!expirationTimersRef.current.has(s.id)) {
                const timer = setTimeout(() => {
                  setStrokes(prev => prev.filter(p => p.id !== s.id));
                  expirationTimersRef.current.delete(s.id);
                }, remaining);
                expirationTimersRef.current.set(s.id, timer);
              }
            });
            playSound('./sounds/draw.mp3', 0.4);
          })
          .catch(() => {});
      } else if (message.t === 'canvas.clear') {
        expirationTimersRef.current.forEach(timer => clearTimeout(timer));
        expirationTimersRef.current.clear();
        setStrokes([]);
      } else if (message.t === 'submission.new') {
        if (twitch.isModerator) {
          playSound('./sounds/alert.wav', 0.6);
          ebsClient.getPendingSubmissions()
            .then(res => setPendingSubmissions(res.submissions))
            .catch(() => {});
        }
      } else if (message.t === 'submission.resolved') {
        if (twitch.isModerator) {
          ebsClient.getPendingSubmissions()
            .then(res => setPendingSubmissions(res.submissions))
            .catch(() => {});
        }
      }
    });
  }, [twitch, scheduleStrokeExpiration, setStrokes, setPendingSubmissions]);

  useEffect(() => {
    if (drawing.appState !== 'processing') return;
    const timer = setTimeout(() => {
      setTotalAmount(0);
      setAppState('drawing');
      showStatus('Transaction timed out. Please try again.');
    }, 60000);
    return () => clearTimeout(timer);
  }, [drawing.appState, setTotalAmount, setAppState, showStatus]);

  useEffect(() => {
    twitch.onTransaction(async (tx) => {
      if (submittedTxRef.current.has(tx.transactionId)) return;
      submittedTxRef.current.add(tx.transactionId);

      const strokesToSend = pendingStrokesRef.current;
      if (strokesToSend.length === 0) {
        setTotalAmount(0);
        setAppState('drawing');
        return;
      }

      try {
        const animData = drawing.getAnimationData();
        await ebsClient.commitStrokesBatch(
          strokesToSend,
          tx.transactionId,
          tx.transactionReceipt,
          tx.displayName,
          animData
        );
        if (drawing.isAnimationMode) {
          drawing.handleToggleAnimationMode();
        } else {
          drawing.handleClearPending();
        }
        showStatus('Thanks! Your drawing is waiting for approval.');
      } catch (err) {
        captureException(err, { context: 'commit_strokes' });
        console.error('Failed to commit strokes:', err);
        showStatus('Unable to send drawing. Please try again.');
      }

      setTotalAmount(0);
      setAppState('drawing');
    });

    twitch.onTransactionCancelled(() => {
      setTotalAmount(0);
      setAppState('drawing');
      showStatus('Bits transaction cancelled.');
    });
  }, [twitch, pendingStrokesRef, setPendingStrokes, setTotalAmount, setAppState, showStatus, captureException]);

  const handleConfirmTransaction = useCallback(() => {
    if (identityRequired) return;
    const { sku } = getSkuForAmount(drawing.totalAmount);
    twitch.useBits(sku);
    setAppState('processing');
  }, [identityRequired, drawing.totalAmount, twitch, setAppState]);

  const handleApproveSubmission = useCallback(async (submissionId: string) => {
    setSubmissionBusy(submissionId, true);
    setModError(null);
    try {
      await ebsClient.approveSubmission(submissionId);
      setPendingSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
      showStatus('Drawing approved and sent to viewers.');
    } catch (err) {
      captureException(err, { context: 'approve_submission', submissionId });
      console.error('Failed to approve submission:', err);
      setModError(err instanceof Error ? err.message : 'Failed to approve submission');
    } finally {
      setSubmissionBusy(submissionId, false);
    }
  }, [setSubmissionBusy, showStatus, setPendingSubmissions, captureException]);

  const handleRejectSubmission = useCallback(async (submissionId: string) => {
    setSubmissionBusy(submissionId, true);
    setModError(null);
    try {
      await ebsClient.rejectSubmission(submissionId);
      setPendingSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
      showStatus('Drawing rejected.');
    } catch (err) {
      captureException(err, { context: 'reject_submission', submissionId });
      console.error('Failed to reject submission:', err);
      setModError(err instanceof Error ? err.message : 'Failed to reject submission');
    } finally {
      setSubmissionBusy(submissionId, false);
    }
  }, [setSubmissionBusy, showStatus, setPendingSubmissions, captureException]);

  const handleConsentContinue = useCallback(() => {
    if (!consentChecked) return;
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
    } catch {}
    setConsentAccepted(true);
    setConsentChecked(false);
    twitch.requestIdShare();
  }, [consentChecked, twitch]);

  if (twitch.error) {
    return <div className="error">{twitch.error}</div>;
  }

  if (drawing.appState === 'loading') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div
      className="overlay-container"
      onMouseEnter={drawing.handleMouseEnter}
      onMouseLeave={drawing.handleMouseLeave}
    >
      <DrawCanvas
        strokes={drawing.allStrokes}
        currentStroke={drawing.currentStroke}
        onStartStroke={drawing.handleStartStroke}
        onContinueStroke={drawing.handleContinueStroke}
        onEndStroke={drawing.handleEndStroke}
        disabled={drawing.appState !== 'drawing' || identityRequired}
        isEraser={drawing.isEraser}
        color={drawing.color}
        onionSkin={drawing.onionSkinData}
      />

      <ActiveDrawingsPanel
        strokes={drawing.strokes}
        displayDurationMs={displayDurationMs}
        position={channelConfig?.liveDrawingsPosition ?? 'right'}
      />

      {drawing.showToolbar && (
        <Toolbar
          color={drawing.color}
          width={drawing.width}
          onColorChange={drawing.setColor}
          onWidthChange={drawing.setWidth}
          disabled={drawing.appState !== 'drawing' || identityRequired}
          isEraser={drawing.isEraser}
          onEraserToggle={drawing.handleEraserToggle}
          onEraserOff={drawing.handleEraserOff}
          isAnimationMode={drawing.isAnimationMode}
          onToggleAnimationMode={drawing.handleToggleAnimationMode}
        />
      )}

      {drawing.showToolbar && drawing.isAnimationMode && drawing.appState === 'drawing' && (
        <AnimationControls
          frames={drawing.timelineFrames}
          currentFrameIndex={drawing.currentFrameIndex}
          fps={drawing.animationFps}
          isPreviewing={drawing.isPreviewingAnimation}
          maxFrames={8}
          onGoToFrame={drawing.handleGoToFrame}
          onAddFrame={drawing.handleAddFrame}
          onDeleteFrame={drawing.handleDeleteFrame}
          onReorderFrames={drawing.handleReorderFrames}
          onResizeFrame={drawing.handleResizeFrame}
          onSetFps={drawing.handleSetAnimationFps}
          onTogglePreview={drawing.handleTogglePreview}
        />
      )}

      {drawing.showToolbar && drawing.pendingStrokes.length > 0 && drawing.appState === 'drawing' && (
        <div style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 8,
          background: 'rgba(0,0,0,0.8)',
          padding: '12px 16px',
          borderRadius: 8,
          alignItems: 'center',
        }}>
          <span style={{ color: 'white', fontSize: 14 }}>
            {drawing.pendingAmount} Bits
          </span>
          <button
            onClick={drawing.handleClearPending}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: 4,
              background: '#555',
              color: 'white',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
          <button
            onClick={drawing.handleSubmit}
            style={{
              padding: '6px 16px',
              border: 'none',
              borderRadius: 4,
              background: '#9147ff',
              color: 'white',
              fontSize: 14,
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            disabled={identityRequired}
          >
            Send
          </button>
        </div>
      )}

      {drawing.appState === 'confirming' && (
        <ConfirmDialog
          bits={drawing.totalAmount}
          onConfirm={handleConfirmTransaction}
          onCancel={drawing.handleCancelTransaction}
        />
      )}

      {twitch.isModerator && (
        <ModerationPanel
          submissions={pendingSubmissions}
          onApprove={handleApproveSubmission}
          onReject={handleRejectSubmission}
          busyMap={modActionMap}
          error={modError}
        />
      )}

      {showConsentModal && (
        <div className="identity-overlay">
          <h3>Share your Twitch identity to submit</h3>
          <p>To submit drawings, this Extension needs to associate your Twitch username with your content so it can be moderated and displayed correctly.</p>
          <label className="consent-checkbox">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            <span>I agree to share my Twitch user ID and username with this Extension.</span>
          </label>
          <div className="consent-buttons">
            <button className="consent-cancel" onClick={() => setConsentDismissed(true)}>
              Cancel
            </button>
            <button
              className="consent-continue"
              disabled={!consentChecked}
              onClick={handleConsentContinue}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {statusMessage && (
        <div className="status-toast">
          {statusMessage}
        </div>
      )}
    </div>
  );
}
